// src/components/imagery/TimeStrip/TimeStrip.jsx
//
// The time strip's core: two lanes, one cursor, a compare epoch. It owns no
// state and fetches nothing; an adapter gives it the lanes and keeps the cursor
// (the Review adapter keeps ReviewMap's fixed-view rule, timeStripModel.js has
// the rules). Explore is not on it yet (build step 8).
//
// The reference lane is drawn, labeled "context only", and selectable: looking
// at Sentinel-2 is allowed, judging on it is not, and the page's verdict gate
// says so where the buttons are.

import { normalizeLanes, formatEpoch, COLLECTS_EMPTY_TEXT } from "./timeStripModel";

function Lane({ title, hint, epochs, kind, cursorKey, itemKey, compareKey, onCursor, onCompare, emptyText }) {
  return (
    <div className="flex items-center gap-2" data-testid={`timestrip-lane-${kind}`}>
      <div className="w-36 shrink-0">
        <div className="text-xs font-medium text-fg-primary">{title}</div>
        {hint && <div className="text-[11px] text-fg-secondary">{hint}</div>}
      </div>
      <div className="flex flex-wrap gap-1" role="group" aria-label={title}>
        {epochs.length === 0 && <span className="text-xs text-fg-secondary">{emptyText}</span>}
        {epochs.map((e) => {
          const isCursor = e.dataset_key === cursorKey;
          const isItem = e.dataset_key === itemKey;
          const isCompare = e.dataset_key === compareKey;
          return (
            <span key={e.dataset_key} className="inline-flex items-center">
              <button
                type="button"
                data-testid={`timestrip-epoch-${e.dataset_key}`}
                data-lane={kind}
                aria-pressed={isCursor}
                title={`${e.dataset_key}${e.lod ? ` (${e.lod})` : ""}`}
                onClick={() => onCursor?.(e.dataset_key)}
                className={[
                  "rounded px-2 py-0.5 text-xs border",
                  isCursor ? "border-accent-action bg-accent-action/15 text-fg-primary" : "border-bg-border text-fg-secondary hover:text-fg-primary",
                  kind === "reference" ? "border-dashed" : "",
                ].join(" ")}
              >
                {formatEpoch(e.acquisition_date)}
                {isItem && <span className="ml-1 text-[10px] uppercase tracking-wide text-accent-action">item</span>}
                {isCompare && <span className="ml-1 text-[10px] uppercase tracking-wide">compare</span>}
              </button>
              {onCompare && kind === "collects" && !isCursor && (
                <button
                  type="button"
                  data-testid={`timestrip-compare-${e.dataset_key}`}
                  aria-pressed={isCompare}
                  aria-label={`Compare with ${formatEpoch(e.acquisition_date)}`}
                  onClick={() => onCompare(isCompare ? null : e.dataset_key)}
                  className="ml-0.5 rounded px-1 text-[10px] text-fg-secondary hover:text-fg-primary"
                >
                  {isCompare ? "×" : "⇆"}
                </button>
              )}
            </span>
          );
        })}
      </div>
    </div>
  );
}

/**
 * @param {object} props
 * @param {{collects: object[], reference: object[]}} props.lanes
 * @param {string|null} props.cursorKey   the dataset on screen
 * @param {string|null} props.itemKey     the epoch the item is about, marked "item"
 * @param {string|null} [props.compareKey]
 * @param {(key: string) => void} props.onCursor
 * @param {(key: string|null) => void} [props.onCompare]  omitted: no compare controls
 * @param {string} [props.emptyText]  what an empty collects lane says; an adapter
 *   that knows WHY it is empty (no entitlement, no coverage) says so here
 */
export default function TimeStrip({ lanes, cursorKey, itemKey, compareKey = null, onCursor, onCompare,
  emptyText = COLLECTS_EMPTY_TEXT }) {
  const l = normalizeLanes(lanes);
  return (
    <div className="flex flex-col gap-1.5" data-testid="timestrip">
      <Lane
        title="10 cm collects" kind="collects" epochs={l.collects}
        cursorKey={cursorKey} itemKey={itemKey} compareKey={compareKey}
        onCursor={onCursor} onCompare={onCompare}
        emptyText={emptyText}
      />
      <Lane
        title="Sentinel-2 annual" hint="context only" kind="reference" epochs={l.reference}
        cursorKey={cursorKey} itemKey={itemKey} compareKey={compareKey}
        onCursor={onCursor}
        emptyText="No Sentinel-2 composite here."
      />
    </div>
  );
}
